import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as Error & { status: number }).status
          if (status >= 400 && status < 500) {
            return false
          }
        }
        return failureCount < 3
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})

// Query keys factory for type-safe query keys
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    kpis: (period?: string) =>
      [...queryKeys.dashboard.all, 'kpis', period] as const,
    revenue: (period?: string) =>
      [...queryKeys.dashboard.all, 'revenue', period] as const,
    trends: (period?: string) =>
      [...queryKeys.dashboard.all, 'trends', period] as const,
    orders: (period?: string) =>
      [...queryKeys.dashboard.all, 'orders', period] as const,
    channels: (period?: string) =>
      [...queryKeys.dashboard.all, 'channels', period] as const,
    topProducts: (limit?: number) =>
      [...queryKeys.dashboard.all, 'top-products', limit] as const,
    geo: () =>
      [...queryKeys.dashboard.all, 'geo'] as const,
    rfmSummary: () =>
      [...queryKeys.dashboard.all, 'rfm-summary'] as const,
    topRegions: (period?: string) =>
      [...queryKeys.dashboard.all, 'top-regions', period] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    model: () => [...queryKeys.analytics.all, 'model'] as const,
    query: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'query', params] as const,
    sales: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'sales', params] as const,
    products: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'products', params] as const,
    customers: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'customers', params] as const,
    compare: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'compare', params] as const,
    // E-Commerce Advanced
    rfm: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'rfm', params] as const,
    clv: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'clv', params] as const,
    inventory: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'inventory', params] as const,
    // SaaS
    mrr: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'mrr', params] as const,
    churn: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'churn', params] as const,
    cac: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'cac', params] as const,
    ltv: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'ltv', params] as const,
    unitEconomics: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'unit-economics', params] as const,
    trialConversion: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'trial-conversion', params] as const,
    // Orders & Fulfillment
    orders: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'orders', params] as const,
    fulfillment: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'fulfillment', params] as const,
    // Restaurant
    service: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'service', params] as const,
    menu: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'menu', params] as const,
    laborCost: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'labor-cost', params] as const,
    recipeProfitability: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'recipe-profitability', params] as const,
    // Logistics
    deliveries: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'deliveries', params] as const,
    fleet: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'fleet', params] as const,
    driverPerformance: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'driver-performance', params] as const,
    customerSatisfaction: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'customer-satisfaction', params] as const,
    // Healthcare
    appointments: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'appointments', params] as const,
    patients: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'patients', params] as const,
    treatments: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'treatments', params] as const,
    // Fitness
    memberships: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'memberships', params] as const,
    attendance: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'attendance', params] as const,
    classes: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'classes', params] as const,
    // Hotel
    occupancy: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'occupancy', params] as const,
    bookings: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'bookings', params] as const,
    revpar: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'revpar', params] as const,
    // Manufacturing
    production: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'production', params] as const,
    oee: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'oee', params] as const,
    defects: (params?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'defects', params] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    list: (params?: { page?: number; limit?: number }) =>
      [...queryKeys.users.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
    stats: () => [...queryKeys.users.all, 'stats'] as const,
  },

  // Connectors
  connectors: {
    all: ['connectors'] as const,
    list: () => [...queryKeys.connectors.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.connectors.all, 'detail', id] as const,
  },

  // Jobs
  jobs: {
    all: ['jobs'] as const,
    list: (params?: { status?: string }) =>
      [...queryKeys.jobs.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.jobs.all, 'detail', id] as const,
  },

  // Tenant
  tenant: {
    all: ['tenant'] as const,
    current: () => [...queryKeys.tenant.all, 'current'] as const,
  },
} as const
